import styled from 'styled-components';

import { typographyCss } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Empty } from '@creditornot/cb-placeholders';
import { Spinner } from '@creditornot/cb-progress';
import { ListingRecommendationItem } from 'common/api-types/recommendations';
import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { DataTable, DataTableProps } from 'common/components/cookbook/DataTable/DataTable';
import { SelectionState } from 'common/components/cookbook/DataTable/types';
import { Stack } from 'common/components/cookbook/Stack';
import { TextLink } from 'common/components/cookbook/TextLink';
import { Thumbnail } from 'common/components/cookbook/Thumbnail';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useUserLocale } from 'common/hooks/useUserLocale';
import { media } from 'common/media';
import { AxiosDelistingRecommendationsError } from 'common/types';
import { formatMoney } from 'common/utils/moneyUtils';
import { formatNumber } from 'common/utils/numberUtils';
import { RecommendationReasons } from 'features/selection-recommendations/common/RecommendationReasons/RecommendationReasons';

import { ListingRecommendationsSubList } from './ListingRecommendationsSubList';

const EmptyStateContainer = styled(ContentContainer)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 64px;
  padding-bottom: 64px;
`;

type Props = {
  groupByCategory: ListingRecommendationItem[];
  isFetching: boolean;
  rowSelection: {
    onRowSelectChange: (value: 'ALL' | SelectionState) => void;
    selectedRows: string[] | 'ALL';
  };
  error: AxiosDelistingRecommendationsError | null;
  isLoading: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  totalCount: number | undefined;
};

const StyledDataTable = styled(DataTable as React.FC<DataTableProps<ListingRecommendationItem>>)`
  @media ${media.lg} {
    [role='cell']:nth-child(2),
    [role='columnheader']:nth-child(2) {
      border-right: 1px solid ${color.border};
      left: 48px;
      position: sticky;
      z-index: 0;
    }
  }
`;

const Tier = styled.div`
  ${typographyCss.footnote}
  color: ${color.textSubdued};
`;

const SpinnerContainer = styled(ContentContainer)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

export const ListingRecommendationsList = ({
  groupByCategory,
  isFetching,
  rowSelection,
  error,
  isLoading,
  hasNextPage,
  totalCount,
  fetchNextPage,
}: Props) => {
  const f = useFormatMessage();
  const locale = useUserLocale();

  if (error) {
    return (
      <ContentContainer>
        <ApiErrorNotification error={error} />
      </ContentContainer>
    );
  }

  if (!groupByCategory.length && !isLoading) {
    return (
      <EmptyStateContainer>
        <Empty
          title={f('selection_recommendations.no_recommendations')}
          description={f('selection_recommendations.no_recommendations_description')}
        />
      </EmptyStateContainer>
    );
  }

  if (isLoading) {
    return (
      <SpinnerContainer>
        <Spinner size="large" />
      </SpinnerContainer>
    );
  }

  return (
    <StyledDataTable
      dataTestId="listing-recommendations-list"
      rowSelection={rowSelection}
      dataSource={groupByCategory}
      stickyHeaderOffset={65}
      hideScrollbars={true}
      isStickyHeader={true}
      fetchOnScroll={{
        hasNextPage: hasNextPage || false,
        onFetchMore: fetchNextPage,
        isFetching,
      }}
      renderSubComponent={(record) =>
        record.tiers?.length ? <ListingRecommendationsSubList parent={record} /> : undefined
      }
      columns={[
        {
          id: 'product',
          header: f('selection_recommendations.recommendations_count', {
            count: formatNumber(locale, totalCount ?? 0),
          }),
          width: 400,
          alignNameVertical: 'middle',
          render: ({ record: { productFamily, tiers } }) => {
            return (
              <Stack direction="row" gap="regularXlarge" alignItems="center">
                <Thumbnail
                  height={40}
                  width={40}
                  imgSrc={productFamily.image?.url ?? null}
                  altText={productFamily.name}
                />
                <Stack gap="narrowSmall">
                  <TextLink
                    target="_blank"
                    to="/catalog"
                    search={{
                      conditions: [`PRODUCT_FAMILY:REFERENCE:${productFamily.id}`],
                    }}
                  >
                    {productFamily.name}
                  </TextLink>
                  <Tier>
                    {f('selection_recommendations.tiers', {
                      count: tiers?.length ?? 0,
                    })}
                  </Tier>
                </Stack>
              </Stack>
            );
          },
        },
        {
          id: 'expectedBusinessImpact',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.expected_business_impact'),
          width: 'auto',
          info: f(
            'selection_recommendations.listing_recommendation_expected_business_impact_column_explanation',
          ),
          render: ({ record: { expectedBusinessImpact } }) =>
            formatMoney(locale, expectedBusinessImpact),
        },
        {
          id: 'reason',
          alignNameVertical: 'middle',
          header: f('selection_recommendations.reason'),
          width: 'auto',
          render: ({ record: { reason } }) => {
            return <RecommendationReasons reasons={[reason]} />;
          },
        },
        {
          id: 'revenueShare',
          header: f('selection_recommendations.revenue_share_percentage'),
          info: f('selection_recommendations.revenue_share_percentage_explanation'),
          width: 'auto',
          alignContent: 'right',
          alignName: 'right',
          render: ({ record: { revenueShare } }) => `${formatNumber(locale, revenueShare, 2)}%`,
        },
        {
          id: 'globalRevenueShare',
          header: f('selection_recommendations.global_revenue_share_percentage'),
          info: f('selection_recommendations.global_revenue_share_percentage_explanation'),
          width: 'auto',
          alignContent: 'right',
          alignName: 'right',
          render: ({ record: { globalRevenueShare } }) => {
            if (!globalRevenueShare) {
              return '-';
            }

            return `${formatNumber(locale, globalRevenueShare, 2)}%`;
          },
        },
        {
          id: 'selectionCount',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.average_sku_count_across_venues'),
          info: f('selection_recommendations.average_sku_count_across_venues_explanation'),
          width: 'auto',
          render: ({ record: { selectionCount } }) => formatNumber(locale, selectionCount),
        },
        {
          id: 'gapVersusGlobalAverage',
          alignContent: 'right',
          alignName: 'right',
          width: 'auto',
          header: f('selection_recommendations.gap_versus_global_average'),
          render: ({ record: { gapVersusGlobalAverage } }) =>
            formatNumber(locale, gapVersusGlobalAverage),
        },
        {
          id: 'gapVersusBestInClass',
          alignContent: 'right',
          alignName: 'right',
          width: 'auto',
          header: f('selection_recommendations.gap_versus_best_in_class'),
          render: ({ record: { gapVersusBestInClass } }) =>
            formatNumber(locale, gapVersusBestInClass),
        },
      ]}
    />
  );
};
